import _ from 'underscore';
import $ from 'jquery';
import classNames from 'classnames';
import React, { Suspense, useCallback, useMemo, useContext, useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import PropTypes from 'prop-types';
import { PreferenceContext } from '@bingads-webui-react/preference-context';
import { withDefaultStyles } from '@bingads-webui-react/with-default-styles';
import { Scenario, Activity, ActivityContext } from '@bingads-webui-react/hoc-with-signal';
import {
  COPILOT_WIDTH,
  onToggleCopilot,
  useTokenExchange,
  useMSAConversationRestorationWithCookie,
  getDirectLineEndpoint,
  usePerfMarker,
  MSAGlobalErrorView,
  MSAGlobalLoadingView,
  fetchAppBasedChannelToken,
  getLwiContexts,
  MSACopilotErrorView,
} from '@bingads-webui-react/msa-copilot-common';
import { withServerContext } from '@bingads-webui-campaign-react/server-context';
import { withPermissions } from '@bingads-webui-react/hoc-with-permissions';
import { isNavigationRefreshOn } from '@bingads-webui-react/preference-utility';
import { notificationApi } from '@bingads-webui-react/notification-center';
import { I18nContext } from '@bingads-webui-react/i18n-context';
import { siteMapPropType } from '@bingads-webui-react/with-site-map';
import { withSiteMap, processor } from '@bingads-webui-campaign-react/with-site-map';
import { LayoutContext, Mode } from '@bingads-webui-react/app-layout-container';
import { InsightsPanelLoader } from '@bingads-webui/copilot-insights';
import {
  WaterfallFilterStorage,
  WaterfallFilterType,
  waterfallDateFilterProcessor
} from '@bingads-webui-campaign/filter-storage';
import { fetchCampaignsData } from '@bingads-webui-react/copilot-troubleshooting-data';
import { fromLegacyTimeZone, toIANATimeZone } from '@bingads-webui-universal/time-zone-converter';
import { withGlobalErrorView } from '@bingads-webui-campaign-react/global-error-options';
import { isCopilotRegionRestricted } from '@bingads-webui-react/copilot-utils';

import { withAppPerfMarker } from '../../contexts/app-perfmarker-context';
import { CopilotTeachingBubble } from './teaching-bubble';
import { useGetIsPilotEnabled } from './use-get-is-pilot-enabled';
import { useCopilotToggleEventListener } from './use-copilot-toggle-event-listener';

const LazyMSAdsCopilot = React.lazy(() => import(/* webpackChunkName: "ads-copilot" */'@bingads-webui-react/msa-copilot'));
const POPUP_ERROR_MESSAGE = 'Oops! It seems your popup blocker is active. Please disable it and try again for the full copilot experience.';
const CopilotLastReaction = 'CopilotLastReaction';
const CopilotLastConversationId = 'CopilotLastConversationId';

const styles = () => ({
  root: {
    right: 0,
    position: 'absolute',
    top: 0,
    bottom: 0,
    minWidth: COPILOT_WIDTH,
    display: 'block',
    flex: 1,
    zIndex: 1,
    height: '100%',
  },
  rootNavRefresh: {
    position: 'relative',
  },
  '@global': {
    '.app-shell-copilot': {
      'pointer-events': 'auto !important',
      '& .teaching-bubble-content': {
        width: '364px',
      },
    },
    '.ads-copilot-preparing-view': {
      'pointer-events': 'auto',
    },
  },
});

export const UnstyledMSAdsCopilotLoader = React.memo((props) => {
  const {
    mode, classes, serverContext, appPerfMarker, permissions, siteMap, changeLayout,
  } = props;
  const activity = useContext(ActivityContext);
  const preferenceService = useContext(PreferenceContext);
  const [cachedPath, setCachedPath] = useState(null);
  const firstName = _.get(serverContext, ['CurrentUser', 'FirstName']);
  const lastName = _.get(serverContext, ['CurrentUser', 'LastName']);
  const userid = useMemo(() => `${_.get(serverContext, ['CurrentUser', 'Id'])}`, [serverContext]);
  const username = useMemo(() => _.get(serverContext, ['CurrentUser', 'Name']), [serverContext]);
  const customerId = useMemo(() => `${_.get(serverContext, ['CurrentCustomer', 'Id'])}`, [serverContext]);
  const accountId = useMemo(() => `${_.get(serverContext, ['CurrentAccount', 'Id'])}`, [serverContext]);
  const ccSmallToken = useMemo(() => `CCMTSmallToken ${_.get(serverContext, ['Token'])}`, [serverContext]);
  const localeId = _.get(serverContext, ['CurrentUser', 'LocaleID']);
  const isAADUser = _.get(serverContext, ['IsAADUser']);
  const currentLocale = _.get(serverContext, ['CurrentLocale']);
  const dateFormat = _.get(serverContext, ['CurrentUser', 'CultureDateTimeFormat', 'ShortDatePattern']);
  const TimeZone = _.get(serverContext, ['CurrentAccount', 'TimeZone']);
  const ianaTimeZone = useMemo(() => toIANATimeZone(fromLegacyTimeZone(TimeZone)), [TimeZone]);
  const isAppBasedAuthEnabled = _.get(permissions, ['dynamic', 'AdsCopilotAppBasedAuth']);
  const isDegradedModeEnabled = _.get(permissions, ['dynamic', 'AdsCopilotDegradedMode']);
  const perfMarker = appPerfMarker.createChild('AdsCopilot');
  const isNavRefresh = isNavigationRefreshOn(permissions);
  const isAdsCopilotLiveChatGlobalizationEnabled = _.get(permissions, ['dynamic', 'AdsCopilotLiveChatGlobalizationEnabled']) || false;
  const isAdsCopilotUxImprovementsEnabled = _.get(permissions, ['dynamic', 'AdsCopilotUxImprovements']) || false;
  const getIsPilotEnabled = useGetIsPilotEnabled({ permissions });
  const location = useLocation();
  const { config: layoutConfig } = useContext(LayoutContext);
  const { copilot: copilotLayoutConfig } = layoutConfig;
  const { hasCustomizedEvent } = copilotLayoutConfig;
  const waterfallFilterStorage = new WaterfallFilterStorage({
    preferenceService,
  });
  const dateRange = waterfallDateFilterProcessor(
    waterfallFilterStorage.getFilters([WaterfallFilterType.DateFilter]),
    serverContext
  );

  useCopilotToggleEventListener({
    userName: username,
    localeId,
    isAADUser,
    isAppBasedAuthEnabled,
    changeLayout,
    logger: activity,
  });

  const lwicontexts = getLwiContexts(serverContext);
  const liveAgentChatConfig = {
    isAADUser,
    surveyContext: {
      firstName,
      lastName,
      localeId: currentLocale,
    },
    lwicontexts,
  };

  const pilots = {
    isCopilotLiveChatGlobalizationEnabled: isAdsCopilotLiveChatGlobalizationEnabled,
  };

  const onAuthPopupError = useCallback(() => {
    notificationApi.showWarning(POPUP_ERROR_MESSAGE);
  }, []);

  const tokenExchangeFn = useTokenExchange({
    userName: username,
    localeId,
    isAADUser,
    logger: activity,
    onAuthPopupError,
    shouldRetry: !!isDegradedModeEnabled,
  });

  const onCopilotModeChange = useCallback(({ mode: newMode }) => {
    onToggleCopilot({ mode: newMode, changeLayout, logger: activity });
  }, [activity, changeLayout]);

  const onSendCustomizedEvent = useCallback(() => {
    const newConfig = {
      copilot: {
        ...copilotLayoutConfig,
        hasCustomizedEvent: false,
      },
    };
    changeLayout(newConfig);
  }, [copilotLayoutConfig, changeLayout]);

  const onFeedbackReaction = useCallback((data) => {
    preferenceService.setAtUserLevel({
      Name: CopilotLastReaction,
      Data: data,
    });
  }, [preferenceService]);

  const onChangeConversationId = useCallback((conversationId) => {
    preferenceService.setAtUserLevel({
      Name: CopilotLastConversationId,
      Data: conversationId,
    });
  }, [preferenceService]);

  const onCopilotClose = useCallback(() => {
    onCopilotModeChange({ mode: 'collapsed' });
    $('.global-menu-copilot-dropdown-toggle').trigger('focus');
  }, [onCopilotModeChange]);

  const botServiceConfig = useMemo(() => {
    const config = {
      tokenApi: '/api/token',
      endpoint: getDirectLineEndpoint(),
      authToken: '',
      userid,
      username,
      customerId,
      accountId,
      ccSmallToken,
      productName: 'webui_expert_mode',
      fetchOAuthToken: tokenExchangeFn,
      ianaTimeZone,
      legacyTimeZone: fromLegacyTimeZone(TimeZone),
    };

    if (isAppBasedAuthEnabled) {
      // OAuth token is not needed for app-based auth, return empty value
      config.fetchOAuthToken = () => Promise.resolve({ token: '' });

      // fetch app-based channel token directly from CCUI
      config.fetchToken = fetchAppBasedChannelToken;

      const urlParams = new URLSearchParams(window.location.search);
      const copilotOneBoxToken = urlParams.get('copilotOneBoxToken');

      if (copilotOneBoxToken) {
        config.fetchOAuthToken = () => Promise.resolve({ token: copilotOneBoxToken });
        config.fetchToken = undefined;
      }
    }

    return config;
  }, [
    accountId,
    ccSmallToken,
    customerId,
    ianaTimeZone,
    isAppBasedAuthEnabled,
    tokenExchangeFn,
    userid,
    username,
    TimeZone,
  ]);

  const getCampaigns = useCallback(() => fetchCampaignsData({ accountId, customerId, ccToken: ccSmallToken }).then((result) => {
    const value = _.get(result, ['data', 'value']);
    if (_.isArray(value)) {
      const campaigns = _.map(value, c => ({ id: c.Id, name: c.Name }));
      return campaigns;
    }
    return activity.error(`[Copilot Reporting Insights] no campaigns returned: ${result.status} ${result.statusText} ${JSON.stringify(result)}`);
  }), [accountId, activity, ccSmallToken, customerId]);

  const {
    token, error, forceRefreshToken, onConnectionStatusChange, onInitDirectLine,
  } = useMSAConversationRestorationWithCookie({
    botServiceConfig,
    mode,
    logger: activity,
    onCopilotModeChange,
    preferenceService,
  });

  const { onMount, onConnectionStatusChange: finalOnConnectionStatusChange } = usePerfMarker({
    perfMarker,
    token,
    mode,
    onConnectionStatusChange,
  });

  const i18n = useContext(I18nContext);
  const className = classNames(classes.root, isNavRefresh && classes.rootNavRefresh);

  const onFullInsightsClick = useCallback((event, replyToId, options) => {
    const {
      onFeedbackButtonClick,
    } = options;

    // NOTE: contentProps will get shallow copied, so any objects with methods (e.g. i18n) will lose its methods
    changeLayout({
      overlay: {
        mode: Mode.Expanded,
        content: InsightsPanelLoader,
        contentProps: {
          dateFormat,
          dateRange,
          i18nProviderProps: {
            locale: currentLocale,
          },
          logger: activity,
          onFeedbackButtonClick,
          replyToId,
          TimeZone,
        },
      },
    });
  }, [
    activity,
    changeLayout,
    currentLocale,
    dateFormat,
    dateRange,
    TimeZone,
  ]);

  const loadingView = useMemo(() => (
    <MSAGlobalLoadingView
      i18n={i18n}
      onCopilotClose={onCopilotClose}
      preferenceService={preferenceService}
      classname={className}
      isDynamicWidthEnabled={isAdsCopilotUxImprovementsEnabled}
    />
  ), [className, i18n, onCopilotClose, preferenceService, isAdsCopilotUxImprovementsEnabled]);

  useEffect(() => {
    const path = _.get(location, 'pathname');
    if (cachedPath !== path) {
      setCachedPath(path);
      activity.trace(`[Copilot] current page path: ${path})}`);
    }
  }, [activity, cachedPath, location]);

  const [isAdsCopilotRegionRestricted, setIsAdsCopilotRegionRestricted] = useState();
  useEffect(() => {
    isCopilotRegionRestricted({
      feature: 'Chat',
      permissions: props.permissions,
      serverContext: props.serverContext,
    }).then(setIsAdsCopilotRegionRestricted);
  }, [props.permissions, props.serverContext]);

  const isCopilotReady = !_.isEmpty(token) && isAdsCopilotRegionRestricted != null;

  if (mode === 'expanded' && error != null && !isDegradedModeEnabled) {
    return (
      <div className={className}>
        <MSAGlobalErrorView
          i18n={i18n}
          onCopilotClose={onCopilotClose}
          onRefresh={forceRefreshToken}
          isUxImprovementsEnabled={isAdsCopilotUxImprovementsEnabled}
        />
      </div>
    );
  }

  if (mode === 'expanded' && !isCopilotReady) {
    return loadingView;
  }

  return mode === 'collapsed' || (!isCopilotReady && !isDegradedModeEnabled) ? null : (
    <Suspense fallback={loadingView}>
      <div className={className}>
        <LazyMSAdsCopilot
          {..._.omit(props, 'classes')}
          onCopilotClose={onCopilotClose}
          token={token}
          botServiceConfig={botServiceConfig}
          logger={activity}
          onMount={onMount}
          onConnectionStatusChange={finalOnConnectionStatusChange}
          onInitDirectLine={onInitDirectLine}
          siteMap={siteMap}
          liveAgentChatConfig={liveAgentChatConfig}
          pilots={pilots}
          getIsPilotEnabled={getIsPilotEnabled}
          locale={currentLocale}
          hasCustomizedEvent={hasCustomizedEvent}
          onSendCustomizedEvent={onSendCustomizedEvent}
          onFeedbackReaction={onFeedbackReaction}
          onChangeConversationId={onChangeConversationId}
          bingAdsI18n={i18n}
          onFullInsightsClick={onFullInsightsClick}
          getCampaigns={getCampaigns}
          isLiveAgentOnlyMode={isAdsCopilotRegionRestricted}
        />
      </div>
    </Suspense>
  );
});

UnstyledMSAdsCopilotLoader.propTypes = {
  classes: PropTypes.shape({
    root: PropTypes.string.isRequired,
    rootNavRefresh: PropTypes.string.isRequired,
  }).isRequired,
  changeLayout: PropTypes.func.isRequired,
  serverContext: PropTypes.shape({

  }).isRequired,
  mode: PropTypes.string.isRequired,
  appPerfMarker: PropTypes.shape({
    done: PropTypes.func,
    willInit: PropTypes.func,
    willRender: PropTypes.func,
    createChild: PropTypes.func,
  }).isRequired,
  permissions: PropTypes.shape({
    dynamic: PropTypes.shape({
      NavigationRefresh: PropTypes.bool,
    }),
  }).isRequired,
  siteMap: siteMapPropType.isRequired,
};

const CopilotErrorView = React.memo((props) => {
  const activity = useContext(ActivityContext);
  return (
    <MSACopilotErrorView
      {...props}
      logger={activity}
    />);
});

CopilotErrorView.propTypes = {
  error: PropTypes.instanceOf(Error),
  info: PropTypes.instanceOf(Object),
  errorViewProps: PropTypes.instanceOf(Object),
};

CopilotErrorView.defaultProps = {
  error: null,
  info: null,
  errorViewProps: {},
};

export const MSAdsCopilotLoader = withServerContext(withDefaultStyles(withGlobalErrorView(
  withAppPerfMarker(withSiteMap(UnstyledMSAdsCopilotLoader, [
    processor.processPermissions,
    processor.processGlobalFilter,
    processor.processCampaignType,
    processor.processPath,
  ])),
  {
    renderErrorView: props => <CopilotErrorView {...props} />,
  }
), styles));

const MSAdsCopilotLoaderWithPermission = withPermissions(props => (
  <Scenario name="AdsCopilot">
    <Activity name="UserAction">
      <MSAdsCopilotLoader {...props} />
      <CopilotTeachingBubble changeLayout={props.changeLayout} />
    </Activity>
  </Scenario>
));

MSAdsCopilotLoaderWithPermission.propTypes = {
  permissions: PropTypes.shape({}).isRequired,
};

export default MSAdsCopilotLoaderWithPermission;
